import { bytesToNDigits } from '@mail/cross-sizes-utils';
import classNames from 'clsx';
import { ReactComponent as CalendarIcon } from 'img/icons/subscriptions/calendar-icon.svg';
import { ReactComponent as CardIcon } from 'img/icons/subscriptions/card-icon.svg';
import { ReactComponent as InfoIcon } from 'img/icons/subscriptions/info-icon.svg';
import React, { type VFC } from 'react';
import { useSelector } from 'react-redux';
import { BillingDataPlatform } from 'reactApp/api/billing/data/BillingDataAPICall.types';
import { BASEQUOTA, IS_SOCIAL_USER } from 'reactApp/appHelpers/configHelpers';
import { isDiscountOutflowOldProduct } from 'reactApp/appHelpers/featuresHelpers/features/discountInOutflow';
import { abPauseSubscription } from 'reactApp/appHelpers/featuresHelpers/features/pauseSubscription';
import { PARTNER_TITLE } from 'reactApp/modules/billingData/billing.data';
import { type BillingDataSubscriptionInfo, BillingDataSubscriptionInfoStatus } from 'reactApp/modules/billingData/billingData.types';
import { BuySubscriptionSelectors } from 'reactApp/modules/buySubscription/buySubscription.module';
import { CardSelectors } from 'reactApp/modules/creditCard/creditCard.selectors';
import type { IPropsWithPopup } from 'reactApp/modules/popup/popup.helpers';
import { cheapestProductIDs } from 'reactApp/modules/products/products.const';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { EPromoSubscriptionType, isVkMusicReward } from 'reactApp/modules/promoTariffs/promoTariffs.types';
import { getPausedSubscriptionStorageKey, isPausedSubscription } from 'reactApp/modules/subscriptions/subscriptions.helpers';
import { UserStorageSelectors } from 'reactApp/modules/user/userStorage';
import { isVkMusicSubscription } from 'reactApp/sections/SubscriptionsPage/SubscriptionsPage.helpers';
import { PersonalDiscountBanner } from 'reactApp/sections/SubscriptionsPage/ui/PersonalDiscountPrice/PersonalDiscountPrice';
import type { RootState } from 'reactApp/store';
import type { Product } from 'reactApp/types/Billing';
import { VkMusicBlock } from 'reactApp/ui/Mobile/SubscriptionInfoModal/Components/VkMusicBlock/VkMusicBlock';
import { getTypeOfPeriodName } from 'reactApp/utils/Period';

import { CommonInfo } from '../CommonInfo/CommonInfo';
import { SubscriptionInfoItem } from '../SubscriptionInfoItem/SubscriptionInfoItem';
import { useHandlers } from './hooks/useHandlers';
import styles from './ModernSubscription.css';
import { getCardNumber, getExpiresTitle, getNextPayment, getTariffConditionsText } from './ModernSubscription.helpers';

type Props = IPropsWithPopup<{ subscription: BillingDataSubscriptionInfo; isRebrandingQuotaLanding?: boolean; isRebranding?: boolean }>;

export const ModernSubscription: VFC<Props> = ({ subscription, onClose, isRebrandingQuotaLanding, isRebranding = false }) => {
    const vkMusicSubscription = isVkMusicSubscription(subscription);
    const vkMusicReward = isVkMusicReward(subscription);
    const showDefaultHeader = !vkMusicSubscription;
    const status = subscription.status as BillingDataSubscriptionInfoStatus;
    const { expires, type, renewAt, productId, autorenewal, partner, space, accountLink } = subscription;
    const { card } = useSelector(CardSelectors.getCard);
    const cardNumber = getCardNumber(card);
    const paymentMethod = card?.isSbp ? 'Система быстрых платежей' : cardNumber || '';
    const pausedSubscription: boolean =
        useSelector((state: RootState) => UserStorageSelectors.get(state, getPausedSubscriptionStorageKey(subscription.id))) &&
        isPausedSubscription(abPauseSubscription, subscription.renewAt);
    const nextPayment = getNextPayment(renewAt, expires, pausedSubscription);
    // TODO: если id невалидный, то product будет undefined и всё посыпется. Стоит предусмотреть и убрать as Product
    const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, productId)) as Product;
    const conditions = getTariffConditionsText(subscription, product);

    const isMobileOsType = [BillingDataPlatform.ios, BillingDataPlatform.android, BillingDataPlatform.rustore].includes(type);
    const showCard = !isMobileOsType;
    // пока скрываем для мобильных подписок, тк плашка отображается некорректно
    const showMainInfo = type === BillingDataPlatform.web || vkMusicSubscription;
    const showNextPayment = (typeof autorenewal === 'boolean' || vkMusicSubscription) && showCard;
    const showExpires = showDefaultHeader && expires && (partner || status === BillingDataSubscriptionInfoStatus.SUSPENDED);

    const { handleUpdateCard, handleCancelNextPayment, handleGoToSubscription, handleRenewNextPayment, handlePartnerClick } = useHandlers(
        subscription,
        onClose
    );

    const subscriptionType = subscription.type as BillingDataPlatform | EPromoSubscriptionType;

    // tempexp_16598-start
    const isCheapest = productId && Object.values(cheapestProductIDs).includes(productId);
    const tariffSpace = isCheapest ? bytesToNDigits(BASEQUOTA, 3) : space;
    const customTitle = isCheapest ? `${bytesToNDigits(BASEQUOTA, 3).value} без рекламы` : undefined;
    // tempexp_16598-end

    let discountBanner;
    let discountInOutflowConditions;
    const canSetDiscountOutflow = useSelector(BuySubscriptionSelectors.getCanSetDiscountInOutflow);
    const discountOutflowPercent = useSelector(BuySubscriptionSelectors.getDiscountInOutflowPercent);
    const discountOutflowPrice = useSelector(BuySubscriptionSelectors.getDiscountInOutflow);
    const isOldFlowDiscountAbProduct = isDiscountOutflowOldProduct(productId);
    // поддержка старого флоу экспа скидка в оттоке
    if (isOldFlowDiscountAbProduct) {
        discountBanner = <PersonalDiscountBanner />;
        discountInOutflowConditions = conditions;
    }
    if (!canSetDiscountOutflow && discountOutflowPrice && discountOutflowPercent) {
        discountBanner = <PersonalDiscountBanner />;
        const discountConditions = `${discountOutflowPrice} ₽ в ${getTypeOfPeriodName(product?.period)}`;
        discountInOutflowConditions = product.price ? (
            <div className={styles.discountPriceContainer}>
                <span className={styles.crossedText}>{product.price} ₽</span>
                <span>{discountConditions}</span>
            </div>
        ) : (
            discountConditions
        );
    }

    // tempexp_17360-next-line
    let giftLabel;
    if (product?.discountPeriod === '13m') {
        giftLabel = '30 дней';
    }

    return (
        <>
            {showDefaultHeader && (
                <CommonInfo
                    space={tariffSpace}
                    status={status as BillingDataSubscriptionInfoStatus}
                    customTitle={customTitle}
                    isRebrandingQuotaLanding={isRebrandingQuotaLanding}
                />
            )}
            {showExpires && (
                <span className={classNames(styles.expires, { [styles.expires_warning]: status === 'SUSPENDED' })}>
                    {getExpiresTitle(expires, status)}
                </span>
            )}
            {showDefaultHeader && subscriptionType === EPromoSubscriptionType.quota && (
                <span
                    className={classNames(styles.tariffManagement, {
                        [styles.tariffManagement_rebrandingQuotaLanding]: isRebrandingQuotaLanding,
                    })}
                >
                    Это подарочная подписка
                </span>
            )}
            {showDefaultHeader &&
                partner &&
                (accountLink ? (
                    <div
                        className={classNames(styles.tariffManagement, {
                            [styles.tariffManagement_rebrandingQuotaLanding]: isRebrandingQuotaLanding,
                        })}
                    >
                        Управление тарифом доступно в личном кабинете&nbsp;
                        <a className={styles.link} onClick={handlePartnerClick} target="_blank" href={accountLink} rel="noreferrer">
                            {PARTNER_TITLE[partner]}
                        </a>
                    </div>
                ) : null)}

            {showMainInfo && (
                <div className={styles.mainInfo}>
                    {vkMusicSubscription && (
                        <VkMusicBlock accountLink={subscription.accountLink} isReward={vkMusicReward} product={product} space={space} />
                    )}
                    <SubscriptionInfoItem
                        title="Условия подписки"
                        subtitle={discountInOutflowConditions || conditions}
                        discountBanner={discountBanner}
                        buttonText="Добавить место"
                        Icon={InfoIcon}
                        onClick={handleGoToSubscription}
                        isRebrandingQuotaLanding={isRebrandingQuotaLanding}
                        giftLabel={giftLabel}
                        showButton={!vkMusicReward}
                        heightByContent={vkMusicReward}
                    />
                    {showNextPayment && (
                        <SubscriptionInfoItem
                            title={autorenewal ? 'Следующее списание' : 'Дата окончания'}
                            subtitle={nextPayment}
                            buttonText={autorenewal ? 'Отключить' : 'Восстановить'}
                            Icon={CalendarIcon}
                            onClick={
                                autorenewal ? () => handleCancelNextPayment(isRebrandingQuotaLanding, isRebranding) : handleRenewNextPayment
                            }
                            isRebrandingQuotaLanding={isRebrandingQuotaLanding}
                            showButton={!vkMusicReward && (autorenewal || !IS_SOCIAL_USER)}
                            heightByContent={vkMusicReward}
                        />
                    )}
                    {showCard && (
                        <SubscriptionInfoItem
                            title="Способ оплаты"
                            subtitle={cardNumber !== null ? paymentMethod : 'Карта не привязана'}
                            buttonText={cardNumber !== null ? `Изменить${card?.isSbp ? '' : ' карту'}` : 'Привязать карту'}
                            Icon={CardIcon}
                            onClick={handleUpdateCard}
                            isRebrandingQuotaLanding={isRebrandingQuotaLanding}
                            showButton={!vkMusicReward}
                            heightByContent={vkMusicReward}
                        />
                    )}
                </div>
            )}
        </>
    );
};
